import { diContainer } from '@/coreContainer/container';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { IError } from '@/coreInfrastructureModule/interfaces/IError';
import { FieldState } from '@/corePagesModule/composables/useForm';
import { Ref } from 'vue';

/**
 * フィールド値を取得するUseCaseの型
 */
type getFieldUseCase = {
  execute(value: string): Promise<INetworkResult<string | undefined, IError>>;
};

/**
 * useFormのFieldStateを更新するomposable
 * @param fieldState - useFormのフィールドステート
 * @param isEdited - 編集済みかどうかの
 */
export const useUpdateFieldState = (fieldState: FieldState, isEdited?: Ref<boolean>) => {
  /**
   * 汎用的な値設定関数
   * @param useCase - 使用するユースケース
   * @param sourceKey - 取得元のキー
   * @param targetKey - 設定先のキー
   */
  const setFieldValue = async (useCase: getFieldUseCase, sourceKey: string, targetKey: string): Promise<void> => {
    if (fieldState[sourceKey].value.value === '') return; // 空文字（選択を外す）なら何もしない

    const requestedValue = fieldState[sourceKey].value.value;

    const result: INetworkResult<string | undefined, IError> = await useCase.execute(requestedValue);

    // マッチする値が存在しない場合は何もしない
    if (result.status.type === 'success' && result.status.data !== undefined && fieldState[sourceKey].value.value === requestedValue) {
      fieldState[targetKey].setValue(result.status.data);
    }
  };
  /**
   * setFieldValueを包む
   * @param useCase - 使用するユースケース
   * @param sourceKey - 取得元のキー
   * @param targetKey - 設定先のキー
   */
  const createSetter = (useCase: getFieldUseCase, sourceKey: string, targetKey: string) => {
    return () => setFieldValue(useCase, sourceKey, targetKey);
  };

  const keyMap = {
    businessNumber: createSetter(diContainer.getBusinessNameUseCase, 'businessNumber', 'businessName'),
    businessName: createSetter(diContainer.getBusinessNumberUseCase, 'businessName', 'businessNumber'),
    warehouseNumber: createSetter(diContainer.getWarehouseNameUseCase, 'warehouseNumber', 'warehouseName'),
    warehouseName: createSetter(diContainer.getWarehouseNumberUseCase, 'warehouseName', 'warehouseNumber'),
    supplierNumber: createSetter(diContainer.getSupplierNameUseCase, 'supplierNumber', 'supplierName'),
    supplierName: createSetter(diContainer.getSupplierNumberUseCase, 'supplierName', 'supplierNumber'),
    customerNumber: createSetter(diContainer.getCustomerNameUseCase, 'customerNumber', 'customerName'),
    customerName: createSetter(diContainer.getCustomerNumberUseCase, 'customerName', 'customerNumber'),
    salesDestinationNumber: createSetter(diContainer.getSalesDestinationNameUseCase, 'salesDestinationNumber', 'salesDestinationName'),
    salesDestinationName: createSetter(diContainer.getSalesDestinationNumberUseCase, 'salesDestinationName', 'salesDestinationNumber'),
  };

  /**
   * フォームの値を変更した際の処理
   * @param option - フォームの種類
   * @param value - 値
   */
  const updateValue = (option: string, value: string) => {
    if (isEdited) isEdited.value = true;
    fieldState[option].setValue(value);
    if (keyMap[option]) keyMap[option]();
  };

  /**
   * 担当者フォームの値を変更した際の処理
   * @param option - フォームの種類
   * @param value - 値
   */
  const updateUserValue = (option: string, value: string[]) => {
    if (isEdited) isEdited.value = true;
    fieldState[option].setValue(value.join(','));
  };

  /**
   * 新規選択肢追加ボタンを押した際の処理
   * @param type - 選択肢の種類
   * @param name - 名前
   * @param code - コード(Package, Manufacturer, EndUserはなし)
   */
  const addNewOption = (type: string, name: string, code?: string) => {
    switch (type) {
      case 'package':
      case 'manufacturer':
      case 'endUser':
        fieldState[type].setValue(name);
        break;
      default:
        if (!code) {
          console.error('エラー:取引編集>新規選択肢追加:Codeなし');
          return;
        }
        fieldState[type + 'Name'].setValue(name);
        fieldState[type + 'Number'].setValue(code);
    }
    if (isEdited) isEdited.value = true;
    return;
  };

  return {
    addNewOption,
    updateValue,
    updateUserValue,
  };
};
